/* ==========================================================================
   filmbro-course.js — the Lifetime bonus course page.

   THE GATE HERE IS NOT THE SECURITY. The Worker is. This file hides the page
   from someone who should not see it, which is courtesy and tidiness; what
   actually protects the lessons is that /course refuses to sign a playback
   URL for an account whose profiles.plan is not 'lifetime'. Anyone can read
   this file. Nobody can forge the plan, because sql/lockdown.sql revoked the
   table-wide UPDATE on profiles.

   So: never put a lesson URL in the page. The page holds slugs. A slug is
   worth nothing without a token.

   THE WORKER CONTRACT

     GET <worker>/course?lesson=<slug>   Authorization: Bearer <supabase jwt>
       200  { src, type: 'hls' | 'mp4', poster?, expires }
       403  { reason: 'plan' }
       401  no or bad token

   Cloudflare Stream, self-hosted HLS in R2 and a signed MP4 all sit behind
   that shape, so changing host later is a Worker change, not a page change.
   ========================================================================== */
(function () {
  if (window.__fbCourseInit) return;
  window.__fbCourseInit = true;

  /* hls.js, only fetched when the browser cannot play HLS natively (Safari
     and iOS can; Chrome and Firefox cannot) and only when a lesson actually
     comes back as HLS. Pinned, because an unpinned CDN script is someone
     else's deploy button on your site. */
  var HLS_SRC = 'https://cdn.jsdelivr.net/npm/hls.js@1.5.17/dist/hls.min.js';
  var _hlsLoad = null;
  function loadHls() {
    if (window.Hls) return Promise.resolve(window.Hls);
    if (_hlsLoad) return _hlsLoad;
    _hlsLoad = new Promise(function (resolve, reject) {
      var s = document.createElement('script');
      s.src = HLS_SRC;
      s.onload = function () { resolve(window.Hls); };
      s.onerror = function () { reject(new Error('hls.js failed to load')); };
      document.head.appendChild(s);
    });
    return _hlsLoad;
  }

  function jwt() {
    try {
      var key = Object.keys(localStorage).find(function (k) {
        return k.indexOf('sb-') === 0 && k.slice(-11) === '-auth-token';
      });
      if (!key) return null;
      var s = JSON.parse(localStorage.getItem(key));
      return (s && s.access_token) || null;
    } catch (e) { return null; }
  }

  function init(root) {
    if (!root || root.__fb) return;
    root.__fb = true;

    var worker = (root.getAttribute('data-worker') || '').replace(/\/+$/, '');
    var urlNoPlan = root.getAttribute('data-no-plan') || '/pages/offer-lifetime-v2';
    var urlNoAuth = root.getAttribute('data-no-auth') || urlNoPlan;

    var player = root.querySelector('[data-fb-player]');
    var video = root.querySelector('[data-fb-video]');
    var errBox = root.querySelector('[data-fb-err]');
    var nowI = root.querySelector('[data-fb-now-i]');
    var nowT = root.querySelector('[data-fb-now-t]');
    var nowS = root.querySelector('[data-fb-now-s]');
    var bPrev = root.querySelector('[data-fb-prev]');
    var bNext = root.querySelector('[data-fb-next]');
    var items = [].slice.call(root.querySelectorAll('[data-lesson]'));
    if (!items.length) return;

    var hls = null;
    var at = -1;

    /* ── where someone got to, per device ──────────────────────────────
       localStorage, not the database: there is no course_progress table
       and inventing one for v1 is not worth a migration. The cost is that
       progress does not follow someone to their phone, which is worth
       saying out loud on the page rather than letting them discover it. */
    function mark(slug) { return 'fb-course:' + slug; }
    function getPos(slug) {
      try { return JSON.parse(localStorage.getItem(mark(slug))) || null; } catch (e) { return null; }
    }
    function setPos(slug, t, dur) {
      try {
        localStorage.setItem(mark(slug), JSON.stringify({ t: Math.round(t), d: Math.round(dur || 0) }));
      } catch (e) {}
    }

    function paintList() {
      items.forEach(function (li, i) {
        var slug = li.getAttribute('data-lesson');
        var p = getPos(slug);
        li.classList.toggle('is-on', i === at);
        var done = p && p.d && p.t / p.d > 0.9;
        li.classList.toggle('is-done', !!done);
        var bar = li.querySelector('.fb-les__p');
        if (bar) {
          var pct = p && p.d ? Math.min(100, Math.round((p.t / p.d) * 100)) : 0;
          bar.style.display = pct > 2 && !done ? '' : 'none';
          bar.firstElementChild.style.width = pct + '%';
        }
      });
      if (bPrev) bPrev.disabled = at <= 0;
      if (bNext) bNext.disabled = at >= items.length - 1;
    }

    /* ── downloads ──────────────────────────────────────────────────────
       A panel exists for every lesson; one that holds no button stays
       hidden, which is why emptiness is decided here rather than in Liquid. */
    var panels = [].slice.call(root.querySelectorAll('[data-files-for]'));
    function showFiles(i) {
      panels.forEach(function (p) {
        var mine = Number(p.getAttribute('data-files-for')) === i;
        p.hidden = !(mine && p.querySelector('[data-fb-dl]'));
      });
    }

    root.addEventListener('click', function (e) {
      var btn = e.target.closest ? e.target.closest('[data-fb-dl]') : null;
      if (!btn || !root.contains(btn)) return;
      e.preventDefault();
      if (btn.__busy) return;

      var tag = btn.querySelector('[data-fb-dl-label]');
      var original = tag ? tag.textContent : '';
      function done(msg, bad) {
        btn.__busy = false;
        btn.disabled = false;
        btn.classList.toggle('is-err', !!bad);
        if (tag) tag.textContent = msg || original;
        if (msg) setTimeout(function () {
          btn.classList.remove('is-err');
          if (tag) tag.textContent = original;
        }, 3500);
      }

      btn.__busy = true;
      btn.disabled = true;
      if (tag) tag.textContent = 'Preparing';

      var t = jwt();
      if (!t) return done('Sign in again', true);

      fetch(worker + '/course?file=' + encodeURIComponent(btn.getAttribute('data-fb-dl')), {
        headers: { 'Authorization': 'Bearer ' + t }
      })
        .then(function (r) {
          if (r.status === 403) throw new Error('Lifetime only');
          if (r.status === 401) throw new Error('Sign in again');
          if (r.status === 429) throw new Error('Try again shortly');
          if (!r.ok) throw new Error('Not available');
          return r.json();
        })
        .then(function (d) {
          var href = d && (d.url || d.src);
          if (!href) throw new Error('Not available');
          /* The Worker must sign these with response-content-disposition set
             to attachment. When it does, the click downloads and the lesson
             keeps playing. target="_blank" is the insurance: if a URL ever
             comes back without that header the browser opens a throwaway tab
             — which closes itself the moment a download starts — instead of
             navigating this page away mid-lesson. */
          var a = document.createElement('a');
          a.href = href;
          a.target = '_blank';
          a.rel = 'noopener';
          if (d.filename) a.download = d.filename;
          document.body.appendChild(a);
          a.click();
          a.remove();
          done();
        })
        .catch(function (err) { done((err && err.message) || 'Not available', true); });
    });

    function fail(msg) {
      player.classList.remove('is-busy');
      player.classList.add('is-error');
      if (errBox) errBox.textContent = msg;
    }

    /* Clearing the source has to be guarded. Calling load() on a <video> whose
       src is already empty raises MediaError "no supported sources", which
       surfaces as an uncaught error on every lesson switch — noise that makes
       a real playback failure impossible to spot in the console. */
    function detach() {
      if (hls) { try { hls.destroy(); } catch (e) {} hls = null; }
      if (video.getAttribute('src')) {
        video.removeAttribute('src');
        try { video.load(); } catch (e) {}
      }
    }

    function play(i, resume) {
      if (i < 0 || i >= items.length) return;
      var li = items[i];
      var slug = li.getAttribute('data-lesson');
      at = i;
      paintList();

      if (nowI) nowI.textContent = 'Lesson ' + (i + 1) + ' of ' + items.length;
      if (nowT) nowT.textContent = li.getAttribute('data-title') || '';
      if (nowS) nowS.textContent = li.getAttribute('data-summary') || '';
      showFiles(i);

      player.classList.remove('is-error');
      player.classList.add('is-busy');
      detach();

      var t = jwt();
      if (!t) return fail('Your session has expired. Sign in again to keep watching.');

      fetch(worker + '/course?lesson=' + encodeURIComponent(slug), {
        headers: { 'Authorization': 'Bearer ' + t }
      })
        .then(function (r) {
          if (r.status === 403) return r.json().catch(function () { return {}; })
            .then(function () { throw new Error('This lesson is part of Filmbro Lifetime.'); });
          if (r.status === 401) throw new Error('Your session has expired. Sign in again to keep watching.');
          if (!r.ok) throw new Error('That lesson would not load. Try again in a moment.');
          return r.json();
        })
        .then(function (d) {
          if (!d || !d.src) throw new Error('That lesson would not load. Try again in a moment.');
          if (d.poster) video.poster = d.poster;
          /* WHICH PLAYER. The obvious test -- "can the browser play HLS
             natively?" -- is the wrong one, and getting it wrong is silent.
             Several Chromium builds answer "maybe" to canPlayType for
             mpegurl and then cannot actually decode a playlist, so setting
             video.src leaves a player at 0:00 with nothing in the console.

             hls.js's own recommendation is the right order: use Media Source
             Extensions wherever they exist, and fall back to a native src
             only where they do not -- which in practice means iOS Safari,
             the one place native HLS genuinely works and MSE is absent. */
          var useHlsJs = typeof window.MediaSource !== 'undefined' ||
                        typeof window.ManagedMediaSource !== 'undefined';
          if (d.type === 'hls' && useHlsJs) {
            return loadHls().then(function (Hls) {
              if (!Hls || !Hls.isSupported()) throw new Error('This browser cannot play the lesson video.');
              hls = new Hls({ enableWorker: true });

              /* Without this, hls.js swallows everything. A blocked segment,
                 a missing file, a bad manifest -- all of it ends as a player
                 sitting at 0:00 with an empty console, which is the least
                 debuggable failure there is. Fatal errors get surfaced; the
                 rest are logged so they can be read after the fact. */
              hls.on(Hls.Events.ERROR, function (evt, data) {
                var where = (data && data.details) || 'unknown';
                var url = (data && data.frag && data.frag.url) ||
                          (data && data.url) || '';
                if (window.console) {
                  console[data && data.fatal ? 'error' : 'warn'](
                    '[filmbro-course] hls ' + (data && data.type) + '/' + where +
                    (url ? ' <- ' + url.split('?')[0] : ''), data);
                }
                if (!data || !data.fatal) return;
                if (data.type === Hls.ErrorTypes.NETWORK_ERROR) {
                  fail('Could not load the video. If this is the first lesson you ' +
                       'have tried, the storage bucket most likely needs its CORS ' +
                       'rule. See the console for the exact request.');
                } else if (data.type === Hls.ErrorTypes.MEDIA_ERROR) {
                  fail('The video could not be decoded. The lesson may need re-encoding.');
                } else {
                  fail('The video stopped unexpectedly. Reload to try again.');
                }
              });

              hls.loadSource(d.src);
              hls.attachMedia(video);
            });
          }
          video.src = d.src;
        })
        .then(function () {
          player.classList.remove('is-busy');
          var p = resume === false ? null : getPos(slug);
          if (p && p.t > 5 && (!p.d || p.t < p.d - 15)) {
            var seek = function () { try { video.currentTime = p.t; } catch (e) {} };
            if (video.readyState >= 1) seek();
            else video.addEventListener('loadedmetadata', seek, { once: true });
          }
          var go = video.play();
          if (go && go.catch) go.catch(function () {});   /* autoplay blocked is fine */
        })
        .catch(function (e) { fail(e && e.message ? e.message : 'Something went wrong.'); });
    }

    /* Throttled, because timeupdate fires about four times a second and
       localStorage writes are synchronous. */
    /* The native element fails silently too: a src it cannot decode fires
       `error` on the element and nothing else. Without this the player just
       sits at 0:00. */
    video.addEventListener('error', function () {
      var e = video.error;
      if (!e) return;
      if (window.console) console.error('[filmbro-course] video error', e.code, e.message);
      fail(e.code === 4
        ? 'This browser could not play the lesson video.'
        : 'The video stopped unexpectedly. Reload to try again.');
    });

    var last = 0;
    video.addEventListener('timeupdate', function () {
      if (at < 0) return;
      var now = Date.now();
      if (now - last < 3000) return;
      last = now;
      setPos(items[at].getAttribute('data-lesson'), video.currentTime, video.duration);
    });
    video.addEventListener('ended', function () {
      if (at < 0) return;
      setPos(items[at].getAttribute('data-lesson'), video.duration, video.duration);
      paintList();
      if (at < items.length - 1) play(at + 1);
    });

    items.forEach(function (li, i) {
      li.addEventListener('click', function () { play(i); });
    });
    if (bPrev) bPrev.addEventListener('click', function () { play(at - 1); });
    if (bNext) bNext.addEventListener('click', function () { play(at + 1); });

    /* Keyboard, ignored while someone is typing. Space and the arrows are
       what people reach for; the browser already gives them to a focused
       <video>, so this only covers the rest of the page. */
    document.addEventListener('keydown', function (e) {
      var t = e.target;
      if (t && (/^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName) || t.isContentEditable)) return;
      if (!root.classList.contains('is-open')) return;
      if (e.key === ' ' || e.key === 'k') {
        e.preventDefault();
        if (video.paused) video.play().catch(function () {}); else video.pause();
      } else if (e.key === 'ArrowRight') { e.preventDefault(); video.currentTime += 5; }
      else if (e.key === 'ArrowLeft') { e.preventDefault(); video.currentTime -= 5; }
      else if (e.key === 'n' && at < items.length - 1) { play(at + 1); }
      else if (e.key === 'p' && at > 0) { play(at - 1); }
    });

    /* ── the gate ──────────────────────────────────────────────────────
       In the theme editor nothing is ever redirected. The editor renders
       the page in an iframe with no customer session, so a redirect here
       throws the merchant out of the section they are editing — exactly
       the bug that cost an afternoon on the Lifetime page. */
    function open() { root.classList.add('is-open'); paintList(); play(0, true); }

    if (window.Shopify && window.Shopify.designMode) { root.classList.add('is-open'); paintList(); return; }

    /* A missing fbPlan is a theme wiring fault, not a signed-out visitor, and
       the two look identical from here. Say so before redirecting, or the next
       person debugging this sees a bounce with nothing in the console. */
    if (typeof window.fbPlan !== 'function') {
      if (window.console) console.error(
        '[filmbro-course] window.fbPlan is undefined -- snippets/fb-plan-checkout ' +
        'is not loaded on this template. Redirecting as if signed out.');
      window.location.replace(urlNoAuth);
      return;
    }
    window.fbPlan().then(function (plan) {
      if (plan === 'lifetime') open();
      else window.location.replace(plan ? urlNoPlan : urlNoAuth);
    }).catch(function () { window.location.replace(urlNoAuth); });
  }

  function boot(scope) { init((scope || document).querySelector('[data-fb-course]')); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { boot(); });
  else boot();
  document.addEventListener('shopify:section:load', function (e) { boot(e.target); });
}());
